import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import MessageModal from './MessageModal';
import { MessageUser, UserRole } from '../../types/message';
import { api } from '../../utils/api';

interface Student {
  id: string;
  name?: string;
  firstName: string;
  lastName: string;
  grade: number | string;
  schoolId: string;
}

interface Teacher {
  id: string;
  name?: string;
  firstName: string;
  lastName: string;
  schoolId: string;
}

interface StudentMessagingProps {
  userId: string;
  grade: string;
  schoolId: string;
}

const Container = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Section = styled.div`
  margin-bottom: 30px;
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  color: #1a1a1a;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e5e7eb;
`;

const SearchContainer = styled.div`
  margin-bottom: 15px;
  display: flex;
  gap: 10px;
  align-items: center;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.875rem;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #7c3aed;
  }
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.875rem;
  background-color: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #7c3aed;
  }
`;

const UserGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 15px;
`;

const UserCard = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 15px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
`;

const UserInfo = styled.div`
  flex: 1;
`;

const UserName = styled.div`
  font-weight: 600;
  color: #1a1a1a;
`;

const UserGrade = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
`;

const MessageButton = styled.button`
  background: #7c3aed;
  color: white;
  padding: 8px 16px;
  border-radius: 6px;
  border: none;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #6d28d9;
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: 20px;
  color: #6b7280;
  font-style: italic;
`;

const StudentMessaging: React.FC<StudentMessagingProps> = ({ userId, grade, schoolId }) => {
  const [classmates, setClassmates] = useState<Student[]>([]);
  const [schoolmates, setSchoolmates] = useState<Student[]>([]);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [selectedUser, setSelectedUser] = useState<MessageUser | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { user, token } = useAuth();

  // Remove search and filter states
  // const [classmateSearch, setClassmateSearch] = useState('');
  // const [schoolmateSearch, setSchoolmateSearch] = useState('');
  // const [teacherSearch, setTeacherSearch] = useState('');
  const [gradeFilter, setGradeFilter] = useState<string>('all');

  useEffect(() => {
    if (!userId || !grade || !schoolId || !token) return;
    api.get(`/students/classmates?userId=${userId}&grade=${grade}&schoolId=${schoolId}`)
      .then(res => res.data)
      .then(data => setClassmates(
        (data.classmates || []).map((u: any) => ({
          ...u,
          id: u._id || u.id
        }))
      ))
      .catch(err => {
        console.error('Error fetching classmates:', err);
        setClassmates([]);
      });
  }, [userId, grade, schoolId, token]);

  useEffect(() => {
    if (!userId || !grade || !schoolId || !token) return;
    api.get(`/students/schoolmates?userId=${userId}&grade=${grade}&schoolId=${schoolId}`)
      .then(res => res.data)
      .then(data => setSchoolmates(
        (data.schoolmates || []).map((u: any) => ({
          ...u,
          id: u._id || u.id
        }))
      ))
      .catch(err => {
        console.error('Error fetching schoolmates:', err);
        setSchoolmates([]);
      });
  }, [userId, grade, schoolId, token]);

  useEffect(() => {
    if (!userId || !grade || !schoolId || !token) return;
    api.get(`/api/students/teachers?userId=${userId}&grade=${grade}&schoolId=${schoolId}`)
      .then(res => res.data)
      .then(data => setTeachers(
        (data.data || []).map((u: any) => ({
          ...u,
          id: u._id || u.id
        }))
      ))
      .catch(err => {
        console.error('Error fetching teachers:', err);
        setTeachers([]);
      });
  }, [userId, grade, schoolId, token]);

  const handleMessageClick = (user: Student | Teacher, role: UserRole) => {
    setSelectedUser({
      id: user.id,
      name: user.name || ([user.firstName, user.lastName].filter(Boolean).join(' ') || ''),
      role: role
    });
    setIsModalOpen(true);
  };

  // Remove filter functions and use full lists
  // const filterUsers = ...
  // const uniqueGrades = ...

  if (!user) return null;

  return (
    <Container>
      {classmates.length > 0 && (
        <Section>
          <SectionTitle>My Classmates</SectionTitle>
          <UserGrid>
            {classmates.map(classmate => (
              <UserCard key={classmate.id}>
                <UserInfo>
                  <UserName>{('firstName' in classmate ? classmate.firstName : '') + ' ' + ('lastName' in classmate ? classmate.lastName : '')}</UserName>
                  <UserGrade>
                    {'grade' in classmate ? `Grade ${classmate.grade}` : 'Teacher'}
                  </UserGrade>
                </UserInfo>
                <MessageButton onClick={() => handleMessageClick(classmate, 'student')}>
                  Message
                </MessageButton>
              </UserCard>
            ))}
          </UserGrid>
        </Section>
      )}
      {schoolmates.length > 0 && (
        <Section>
          <SectionTitle>My Schoolmates</SectionTitle>
          <UserGrid>
            {schoolmates.map(schoolmate => (
              <UserCard key={schoolmate.id}>
                <UserInfo>
                  <UserName>{('firstName' in schoolmate ? schoolmate.firstName : '') + ' ' + ('lastName' in schoolmate ? schoolmate.lastName : '')}</UserName>
                  <UserGrade>
                    {'grade' in schoolmate ? `Grade ${schoolmate.grade}` : 'Teacher'}
                  </UserGrade>
                </UserInfo>
                <MessageButton onClick={() => handleMessageClick(schoolmate, 'student')}>
                  Message
                </MessageButton>
              </UserCard>
            ))}
          </UserGrid>
        </Section>
      )}
      {teachers.length > 0 && (
        <Section>
          <SectionTitle>My Teachers</SectionTitle>
          <UserGrid>
            {teachers.map(teacher => (
              <UserCard key={teacher.id}>
                <UserInfo>
                  <UserName>{('firstName' in teacher ? teacher.firstName : '') + ' ' + ('lastName' in teacher ? teacher.lastName : '')}</UserName>
                  <UserGrade>Teacher</UserGrade>
                </UserInfo>
                <MessageButton onClick={() => handleMessageClick(teacher, 'teacher')}>
                  Message
                </MessageButton>
              </UserCard>
            ))}
          </UserGrid>
        </Section>
      )}
      {selectedUser && (
        <MessageModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedUser(null);
          }}
          sender={user as MessageUser}
          receiver={selectedUser}
        />
      )}
    </Container>
  );
};

export default StudentMessaging; 